import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl, API } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { MoneyTrail } from "@/components/MoneyTrail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Receipt, Camera, Loader2, Trash2, Pencil, Plus, ShieldCheck, FileText, Sparkles, Check, CalendarDays, Download, Coins,
} from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { key: "bank", label: "Bank transfer" },
  { key: "zelle", label: "Zelle" },
  { key: "venmo", label: "Venmo" },
  { key: "check", label: "Check" },
  { key: "cash", label: "Cash" },
  { key: "portal", label: "Portal" },
  { key: "other", label: "Other" },
];
const METHOD_MAP = Object.fromEntries(METHODS.map((m) => [m.key, m.label]));

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtMoney = (n) => (typeof n === "number" ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const currentPeriod = () => new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
const pdfPrefKey = (pid) => `dd_rent_pdf_${pid}`;

export function RentRecord({ propertyId }) {
  const { isPremium } = useTier();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [includePdf, setIncludePdf] = useState(() => localStorage.getItem(pdfPrefKey(propertyId)) === "1");
  const [trailOpen, setTrailOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isPremium) { setLoading(false); return; }
    try { const { data } = await api.get(`/properties/${propertyId}/rent-payments`); setData(data); }
    catch (e) { console.error("load rent record failed", e); }
    finally { setLoading(false); }
  }, [propertyId, isPremium]);

  useEffect(() => { load(); }, [load]);

  const togglePdf = (v) => {
    setIncludePdf(v);
    localStorage.setItem(pdfPrefKey(propertyId), v ? "1" : "0");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this payment record? This can't be undone.")) return;
    try { await api.delete(`/rent-payments/${id}`); toast.success("Record deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  if (!isPremium) return (
    <PremiumLock title="Rent Proof Tracker" feature="rent_proof" icon={Receipt} testId="rent-locked"
      description="Keep a verified ledger of rent payments to defend against false unpaid-rent claims." />
  );
  if (loading || !data) return null;
  const { entries, coverage, move_in_date } = data;
  const loggedCount = coverage.filter((c) => c.logged).length;

  const exportCsv = () => {
    window.open(`${API}/properties/${propertyId}/rent-payments.csv`, "_blank");
  };

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="rent-record">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><Receipt size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Rent record</h3>
            <p className="text-sm text-white/60">Keep a timestamped, screenshot-backed record of your rent payments — so fabricated arrears can't be deducted from your deposit.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Button size="sm" variant="outline" data-testid="money-trail-btn" onClick={() => setTrailOpen(true)}
            className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10">
            <Coins size={15} className="mr-1.5" /> Money trail
          </Button>
          {entries.length > 0 && (
            <Button size="sm" variant="outline" data-testid="rent-csv-btn" onClick={exportCsv}
              className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10">
              <Download size={15} className="mr-1.5" /> CSV
            </Button>
          )}
          <Button size="sm" data-testid="rent-add-btn" onClick={() => { setEditing(null); setAddOpen(true); }}
            className="rounded-xl bg-brand hover:bg-brand-hover text-white">
            <Plus size={15} className="mr-1.5" /> Add payment
          </Button>
        </div>
      </div>

      {/* Coverage month grid */}
      {!move_in_date ? (
        <p className="mt-4 text-sm text-white/50 flex items-start gap-2" data-testid="rent-no-movein">
          <CalendarDays size={15} className="mt-0.5 shrink-0" />
          Add your move-in date (Edit details) to see a month-by-month coverage view.
        </p>
      ) : coverage.length > 0 && (
        <div className="mt-4" data-testid="rent-coverage">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-white/40">Coverage since move-in</p>
            <p className="text-[11px] text-white/45">{loggedCount} of {coverage.length} months logged</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
            {coverage.map((c) => (
              <div key={c.label} data-testid={`rent-month-${c.year}-${c.month}`}
                className={`rounded-lg px-2 py-1.5 text-center text-[11px] font-medium border ${c.logged
                  ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-200"
                  : "bg-white/5 border-white/10 text-white/40"}`}
                title={c.logged ? "Logged" : "Not logged"}>
                {c.label.replace(/ \d{4}$/, "")}
                <span className="block text-[9px] opacity-60">{c.label.match(/\d{4}$/)?.[0]}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[10px] text-white/40">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/40 border border-emerald-400/40" /> Logged</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-white/10 border border-white/15" /> Not logged</span>
          </div>
        </div>
      )}

      {/* Entries */}
      {entries.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="rent-empty">No payments logged yet. Add a screenshot of a rent payment and we'll read the amount and date for you.</p>
      ) : (
        <div className="mt-4 space-y-2.5" data-testid="rent-entries">
          {entries.map((e) => (
            <div key={e.payment_id} data-testid={`rent-entry-${e.payment_id}`} className="rounded-xl border border-white/10 bg-white/5 p-3.5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{e.period_label}</span>
                    <span className="text-sm text-brand font-semibold">{fmtMoney(e.amount)}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-white/10 text-white/60">{METHOD_MAP[e.method] || e.method}</span>
                  </div>
                  <p className="mt-1 text-[11px] text-white/45 flex flex-wrap items-center gap-x-2">
                    <span>Paid {fmtDate(e.paid_on)}</span>
                    <span className="text-white/25">·</span>
                    <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-300" /> Logged {new Date(e.created_at).toLocaleDateString()}</span>
                  </p>
                  {e.screenshots?.length > 0 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto">
                      {e.screenshots.map((p) => (
                        <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer"
                          data-testid={`rent-shot-${p.photo_id}`}
                          className="h-14 w-14 rounded-lg overflow-hidden border border-white/10 shrink-0">
                          <img src={fileUrl(p.storage_path)} alt="receipt" className="h-full w-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {e.editable && (
                    <Button size="sm" variant="ghost" data-testid={`rent-edit-${e.payment_id}`} onClick={() => { setEditing(e); setAddOpen(true); }}
                      className="rounded-lg h-8 w-8 p-0 text-white/70 hover:bg-white/10" title="Edit (24h window)"><Pencil size={13} /></Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`rent-delete-${e.payment_id}`} onClick={() => remove(e.payment_id)}
                    className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40" title="Delete"><Trash2 size={13} /></Button>
                </div>
              </div>
              {!e.editable && (
                <p className="mt-2 text-[10px] uppercase tracking-wide text-white/30" data-testid={`rent-frozen-${e.payment_id}`}>Frozen for integrity</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <FileText size={15} className="text-white/60 mt-0.5 shrink-0" />
          <span className="text-xs text-white/70">Include this rent record as an appendix in the Evidence Package PDF</span>
        </div>
        <Switch data-testid="rent-pdf-toggle" checked={includePdf} onCheckedChange={togglePdf} />
      </div>

      {addOpen && (
        <PaymentDialog propertyId={propertyId} entry={editing}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }} />
      )}
      {trailOpen && <MoneyTrail propertyId={propertyId} onClose={() => { setTrailOpen(false); load(); }} />}
    </div>
  );
}

function PaymentDialog({ propertyId, entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const [period, setPeriod] = useState(entry?.period_label || currentPeriod());
  const [amount, setAmount] = useState(entry?.amount != null ? String(entry.amount) : "");
  const [paidOn, setPaidOn] = useState(entry?.paid_on?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState(entry?.method || "bank");
  const [photoIds, setPhotoIds] = useState([]);
  const [aiExtracted, setAiExtracted] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/rent-payments/analyze`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoIds((c) => [...c, data.photo_id]);
      const ext = data.ai_extracted || {};
      setAiExtracted(ext);
      let did = false;
      if (ext.amount != null && !amount) { setAmount(String(ext.amount)); did = true; }
      if (ext.date && !entry?.paid_on) { setPaidOn(ext.date); did = true; }
      setPrefilled(did);
      toast.success(did ? "We filled in what we could read — please confirm." : "Screenshot attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read the screenshot.");
    } finally { setAnalyzing(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    const amt = parseFloat(amount);
    if (!period.trim()) { toast.error("Add a period label."); return; }
    if (!amt || amt <= 0) { toast.error("Enter the amount paid."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/rent-payments/${entry.payment_id}`, {
          period_label: period.trim(), amount: amt, paid_on: paidOn, method,
        });
        toast.success("Payment updated");
      } else {
        await api.post(`/properties/${propertyId}/rent-payments`, {
          period_label: period.trim(), amount: amt, paid_on: paidOn, method,
          screenshot_photo_ids: photoIds, ai_extracted: aiExtracted,
        });
        toast.success("Payment logged");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="rent-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit payment" : "Log a rent payment"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Entries can be edited within 24 hours of logging, then they freeze." : "Attach a payment screenshot and we'll read the amount and date. You confirm the final values."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="rent-file-input" onChange={onPickFile} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={analyzing} className="w-full rounded-xl" data-testid="rent-pick-btn">
                {analyzing ? <><Loader2 size={16} className="animate-spin mr-2" /> Reading screenshot…</>
                  : <><Camera size={16} className="mr-2" /> {photoIds.length > 0 ? `${photoIds.length} screenshot${photoIds.length === 1 ? "" : "s"} attached — add another` : "Attach payment screenshot"}</>}
              </Button>
              {prefilled && (
                <p className="mt-1.5 text-[11px] text-emerald-500 flex items-center gap-1" data-testid="rent-prefilled-note">
                  <Sparkles size={12} /> Prefilled from your screenshot — confirm below.
                </p>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Period</Label>
            <Input data-testid="rent-period-input" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. July 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input data-testid="rent-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="1850.00" />
            </div>
            <div>
              <Label className="text-xs">Paid on</Label>
              <Input data-testid="rent-date-input" type="date" value={paidOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setPaidOn(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <select data-testid="rent-method-select" value={method} onChange={(e) => setMethod(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              {METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="rent-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || analyzing} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="rent-save">
            {saving ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : (isEdit ? <><Check size={16} className="mr-1.5" /> Save changes</> : "Log payment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
